// The "cancel this request" confirmation. Extracted from PurchasingQueuePage.js
// at Session 79.14c.
//
// It states the consequence before it asks, and since #592 (gate 391 D17,
// Session 79.21b) it ASKS WHY. The old version marked the request cancelled
// and nothing else: no reason, no name, no time, and the person who asked was
// never told -- a silent cancellation produces the duplicate request the next
// day that gate 391's D7 describes for a silent denial. Now the reason is
// required, the cancel is written by cancel_material_request() with who and
// when, and the asker and the approving manager are told why.
//
// The consequence names WHO will be told before the press (callout P). The
// reason survives an error, because nothing here clears it.

import React, { useState, useEffect } from 'react';
import Overlay from '../Overlay';
import ActionButton from '../ActionButton';

// White on #A32D2D measures 7.07; the grey this sheet used for its body text,
// #7A7A7A, measured 4.29 against the 4.5 floor.
const DANGER = '#A32D2D';

export default function CancelConfirmSheet({ req, isMobile, saving, error, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  // A different request is a different reason -- never carry one across.
  useEffect(() => { setReason(''); }, [req?.id]);

  if (!req) return null;
  const item = req.material_request_items?.[0];
  const itemName = item?.is_off_catalog
    ? (item.off_catalog_description || '—')
    : (item?.inventory_items?.name || '—');

  // Gate 391 F2: "{asker} and {approver} will be told why." The approver is
  // named only when there is one and it is somebody else.
  const asker = req.users?.full_name || 'The person who asked';
  const approver = req.approver?.full_name && req.approved_by !== req.requested_by
    ? req.approver.full_name : null;
  const told = approver ? `${asker} and ${approver} will be told why.` : `${asker} will be told why.`;
  const askerShort = req.users?.full_name ? req.users.full_name.split(' ')[0] : 'They';

  // #250 / #148 Split C, Session 79.14. Converted to Overlay.js - locked rule 3.
  //
  // THE SAVE GUARD IS CARRIED ACROSS DELIBERATELY. This sheet has always refused
  // to close while it is cancelling, and passing `undefined` as onClose is what
  // preserves that: Overlay checks for a handler before firing, so the backdrop
  // AND the Escape key both go dead for exactly as long as the write is in
  // flight. #292 is the precedent - the previous Overlay migration in this
  // codebase silently added two new ways to dismiss something, and it took its
  // own issue to undo.
  //
  // Depth 5000 is kept: this confirmation sits above the action sheets, which
  // sit above the mobile detail sheet.
  return (
    <Overlay
      variant={isMobile ? 'sheet' : 'modal'}
      maxWidth={420}
      zIndex={5000}
      onClose={saving ? undefined : onClose}
      panelStyle={{
        padding: isMobile ? '20px 20px 32px' : '22px 22px 22px', boxSizing: 'border-box',
        borderRadius: isMobile ? '16px 16px 0 0' : 16, // token, not Overlay's 14 - see MarkOrderedSheet
      }}
    >
      <div>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '0 auto 16px' }} />}
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 6 }}>Cancel this request?</div>
        <div style={{ fontSize: 13, color: '#3A3A3A', marginBottom: 14, lineHeight: 1.5 }}>
          {req.request_number} · {itemName}. {told} It comes off this list and can&rsquo;t be undone from this screen.
        </div>
        {/* #642, Session 79.22b -- CANCELING AN ORDER ALREADY PLACED IS A
            DIFFERENT ACT. A purchase order is with the vendor; this records the
            cancel in Stede and tells the people who asked, and it does not stop
            the order. Said before the press, so nobody assumes it does. */}
        {req.status === 'ordered' && (
          <div style={{
            background: '#FDF3E3', border: '1px solid #F3D9A8', borderRadius: 8,
            padding: '9px 11px', fontSize: 13, color: '#6B4200', lineHeight: 1.5, marginBottom: 14,
          }}>
            <strong>This was already ordered{req.po_ref ? ` on ${req.po_ref}` : ''}.</strong>{' '}
            Canceling here does not tell the vendor — contact them to stop the order.
          </div>
        )}

        <label htmlFor="cancel-reason" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#3A3A3A', marginBottom: 5 }}>
          Why? {askerShort} will see this.
        </label>
        <textarea
          id="cancel-reason"
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          disabled={saving}
          // The 16px floor (locked rule 12) and the face both come from
          // index.css's baseline -- #166 and #601.
          style={{ width: '100%', padding: '9px 10px', border: '1px solid #CFCFCF', borderRadius: 8, boxSizing: 'border-box', marginBottom: 12 }}
        />

        {error && <div role="alert" style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <ActionButton
          onClick={() => onConfirm(reason.trim())}
          blockedReason={reason.trim() ? null : `Say why, so ${askerShort === 'They' ? 'they know' : `${askerShort} knows`} what to do next.`}
          reasonStyle={{ fontSize: 12, color: '#48545F', marginTop: 0, marginBottom: 8 }}
          busy={saving}
          busyLabel="Canceling…"
          style={{
            width: '100%', minHeight: isMobile ? 48 : 40, background: DANGER, color: '#FFFFFF',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 8,
          }}
        >
          Cancel request
        </ActionButton>
        <button onClick={onClose} disabled={saving}
          style={{ width: '100%', minHeight: isMobile ? 44 : 36, background: 'none', border: '1px solid #D8D8D8', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#3A3A3A', cursor: saving ? 'not-allowed' : 'pointer' }}>
          Keep it
        </button>
      </div>
    </Overlay>
  );
}

// Issue #191 — three-way match. The physical receive (materialReceive.js) only ever
// touched quantity; this is the separate, retroactive invoice-price entry that closes
// the loop against the price frozen at Mark Ordered time (ordered_price).
// Issue #212 Screen 9. One row per received item, replacing the single request-level price
// this sheet carried before — that box could not say which item its number belonged to once
// a request could hold several. Rebuilt at Session 74k9 onto Overlay.js, which supplies the
// portal, the Escape handler and the desktop-centred presentation; the hand-rolled
// position: fixed it used before is gone.
// Issue #210 — dollar-threshold purchase approval. Admin/executive only. Denial
// reverts the request to 'approved' rather than a terminal state, so purchasing
// can revise and resubmit through Mark Ordered instead of starting over.
